import { useCallback, useMemo, useRef, useState } from 'react';

import { Stack, Typography } from '@mui/material';

import { OrganiseEntryStatus } from '@karehero/models';
import { toLocalDateStr, getEndOfWeek } from '@karehero/helpers/datetime';

import {
  EntryGroup,
  EntryCategory,
  OrganiserEntryGroup,
} from '../OrganiserEntryGroup/OrganiserEntryGroup';
import { OrganiserEntryItem } from '../OrganiserEntryRow/OrganiserEntryRow';
import { ActionStatusFilter } from '../../molecules/ActionStatusTabs/ActionStatusTabs';

const DEFAULT_PAGE_SIZE = 3;
// A checked entry lingers (faded) before it leaves the To do list. Members get
// a longer window per the design ("5 second grace period").
const ADMIN_COMPLETED_GRACE_MS = 2000;
const MEMBER_COMPLETED_GRACE_MS = 5000;

// Admin (multi-care-circle) buckets.
const ENTRY_GROUPS: EntryGroup[] = [
  {
    title: 'Overdue',
    category: EntryCategory.OVERDUE,
    color: 'error.main',
    isOverdue: true,
  },
  {
    title: 'No date',
    category: EntryCategory.UNDATED,
  },
  {
    title: 'Today',
    category: EntryCategory.TODAY,
  },
  {
    title: 'Upcoming',
    category: EntryCategory.UPCOMING,
  },
];

// Member (single care-circle) buckets.
const MEMBER_ENTRY_GROUPS: EntryGroup[] = [
  {
    title: 'Overdue',
    category: EntryCategory.OVERDUE,
    color: 'error.main',
    isOverdue: true,
  },
  {
    title: 'This week',
    category: EntryCategory.THIS_WEEK,
  },
  {
    title: 'No date',
    category: EntryCategory.UNDATED,
  },
  {
    title: 'Upcoming',
    category: EntryCategory.UPCOMING,
  },
];

/** Does this entry pass the member status tab filter? */
const matchesStatusFilter = (
  entry: OrganiserEntryItem,
  filter: ActionStatusFilter,
): boolean => {
  if (filter === 'todo') return !entryIsCompleted(entry);
  if (filter === 'done') return entryIsCompleted(entry);
  return true;
};

const entryIsCompleted = (entry: OrganiserEntryItem) =>
  entry.status === OrganiseEntryStatus.Completed ||
  entry.status === OrganiseEntryStatus.WontDo;

const categoriseEntries = (
  entries: OrganiserEntryItem[],
  categoriseEntry: (e: OrganiserEntryItem) => EntryCategory,
) => {
  const res: { [c: string]: OrganiserEntryItem[] } = {};

  entries.forEach((entry) => {
    const category = categoriseEntry(entry);
    res[category] = [...(res[category] || []), entry];
  });

  return res;
};

interface OrganiserEntryGroupListProps {
  entries: OrganiserEntryItem[];
  onStatusChange: (entryId: string, status: OrganiseEntryStatus) => void;
  onEntryClick: (entryId: string) => void;
  onEntryLinkClick?: (link: string) => void;
  showCompleted?: boolean;
  onShowMore?: (currentlyVisible: number, totalUpcoming: number) => void;
  /** `member` renders the carer buckets (Overdue/Upcoming/No date) + status-tab
   *  filtering; `admin` (default) keeps the multi-care-circle behaviour. */
  variant?: 'admin' | 'member';
  /** Member status tab. Ignored in admin mode. */
  filter?: ActionStatusFilter;
  /** Returns "now" for Overdue/Upcoming bucketing. Defaults to `() => new Date()`;
   *  inject a fixed clock to keep stories/tests deterministic. */
  nowFn?: () => Date;
}

const defaultNowFn = () => new Date();

export const OrganiserEntryGroupList = ({
  entries,
  onStatusChange,
  onEntryClick,
  onEntryLinkClick,
  showCompleted,
  onShowMore,
  variant = 'admin',
  filter = 'todo',
  nowFn = defaultNowFn,
}: OrganiserEntryGroupListProps) => {
  const completedGraceMs =
    variant === 'member' ? MEMBER_COMPLETED_GRACE_MS : ADMIN_COMPLETED_GRACE_MS;

  const [recentlyCompleted, setRecentlyCompleted] = useState<Set<string>>(
    new Set(),
  );
  const [extraPages, setExtraPages] = useState<{ [key: string]: number }>({});

  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleStatusChange = useCallback(
    (entryId: string, status: OrganiseEntryStatus) => {
      if (
        status === OrganiseEntryStatus.Completed ||
        status === OrganiseEntryStatus.WontDo
      ) {
        setRecentlyCompleted((prev) => new Set(prev).add(entryId));

        const existing = timersRef.current.get(entryId);
        if (existing) clearTimeout(existing);

        timersRef.current.set(
          entryId,
          setTimeout(() => {
            setRecentlyCompleted((prev) => {
              const next = new Set(prev);
              next.delete(entryId);
              return next;
            });
            timersRef.current.delete(entryId);
          }, completedGraceMs),
        );
      } else {
        // Unchecking — remove from recently completed immediately
        const existing = timersRef.current.get(entryId);
        if (existing) {
          clearTimeout(existing);
          timersRef.current.delete(entryId);
        }
        setRecentlyCompleted((prev) => {
          const next = new Set(prev);
          next.delete(entryId);
          return next;
        });
      }

      onStatusChange(entryId, status);
    },
    [onStatusChange, recentlyCompleted, setRecentlyCompleted],
  );

  // categorise entries
  const todayStr = useMemo(() => toLocalDateStr(nowFn()), [nowFn]);
  const endOfWeekStr = useMemo(
    () => toLocalDateStr(getEndOfWeek(nowFn())),
    [nowFn],
  );

  const categoriseEntry = useCallback(
    (entry: OrganiserEntryItem) => {
      const key =
        entry.startTime?.slice(0, 10) || entry.occurrenceDate?.slice(0, 10);

      if (!key) {
        return EntryCategory.UNDATED;
      }

      if (key < todayStr) {
        return EntryCategory.OVERDUE;
      }

      if (variant !== 'member') {
        if (key === todayStr) {
          return EntryCategory.TODAY;
        }
        return EntryCategory.UPCOMING;
      }

      if (key <= endOfWeekStr) {
        return EntryCategory.THIS_WEEK;
      }

      return EntryCategory.UPCOMING;
    },
    [todayStr, endOfWeekStr, variant],
  );

  const visibleEntries = useMemo(() => {
    if (variant === 'member') {
      // Filter by the status tab; keep just-checked items briefly (grace).
      return entries.filter(
        (entry) =>
          matchesStatusFilter(entry, filter) ||
          (filter === 'todo' && recentlyCompleted.has(entry.id)),
      );
    }

    if (showCompleted) return entries;

    return entries.filter(
      (entry) => !recentlyCompleted?.has(entry.id) && !entryIsCompleted(entry),
    );
  }, [recentlyCompleted, showCompleted, entries, variant, filter]);

  const categorisedEntries = useMemo(
    () => categoriseEntries(visibleEntries, categoriseEntry),
    [categoriseEntry, visibleEntries],
  );

  const withPagination = useCallback(
    (group: EntryGroup) => {
      const category = group.category;

      const limit =
        DEFAULT_PAGE_SIZE + (extraPages[category] || 0) * DEFAULT_PAGE_SIZE;
      const hasMore = limit < (group.entries?.length || 0);
      const pagedEntries = group.entries?.slice(0, limit);

      const onShowMoreEntries = () =>
        setExtraPages((prev) => {
          onShowMore?.(pagedEntries?.length || 0, group.entries?.length || 0);
          return { ...prev, [category]: (prev[category] || 0) + 1 };
        });

      return {
        ...group,
        entries: pagedEntries,
        hasMore,
        onShowMore: onShowMoreEntries,
      };
    },
    [extraPages, setExtraPages, onShowMore],
  );

  const groups = useMemo(
    () =>
      (variant === 'member' ? MEMBER_ENTRY_GROUPS : ENTRY_GROUPS)
        .map((group) => ({
          ...group,
          entries: categorisedEntries[group.category],
        }))
        .filter((group) => (group.entries?.length || 0) > 0)
        .map(withPagination),
    [categorisedEntries, withPagination, variant],
  );

  if (entries.length === 0) {
    // Member empty state is owned by the page (full "No actions yet" design).
    if (variant === 'member') return null;
    return (
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          py: 6,
        }}
      >
        <Typography
          variant='body1'
          sx={{
            color: 'text.secondary',
          }}
        >
          No actions yet.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      {groups.map((group) => (
        <OrganiserEntryGroup
          {...group}
          key={group.title}
          variant={variant}
          onStatusChange={handleStatusChange}
          onEntryClick={onEntryClick}
          onEntryLinkClick={onEntryLinkClick}
          recentlyCompleted={recentlyCompleted}
        />
      ))}
    </Stack>
  );
};
